import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { EquipmentRegistrationFields, finalizeNumber } from '@/components/registration';
import { engApi, EQUIP_STATUS_LABEL } from './engApi';

const empty = () => ({
  name: '', category: '', kind: 'machine', maker: '', maker_pn: '', model_no: '', serial_no: '',
  location: '', location_id: '', department_id: '', status: 'available', owner: '',
  source_project_id: '', description: '', reference_url: '', acquisition_price: '', acquired_on: '',
  supplier_id: '', supplier_name: '', owner_employee_id: '',
  // TECHS 発注に必要な情報（どの台帳でも同じ項目名）
  number_value: null, sku_group_id: '', order_category_id: '', order_category_name: '', uom: '台',
  inspection_interval_months: '', last_inspection_on: '', next_inspection_on: '', inspection_owner: '',
  calibration_required: false, calibration_interval_months: '', last_calibration_on: '',
  next_calibration_on: '', calibration_owner: '',
});

// 貸出中 は貸出の承認で付く状態。人が選ぶ対象にはしない（既に貸出中の行だけ出す）。
const statusOptions = (cur) => Object.entries(EQUIP_STATUS_LABEL)
  .filter(([k]) => k !== 'rented' || cur === 'rented')
  .map(([value, label]) => ({ value, label }));

/**
 * F2 · 設備を登録 / 編集（技術 設備台帳）。
 *
 * B2 以降、保存先は F3（製造 設備・工具）と**同じひとつの台帳**で、
 * 入力欄は共通部品 `<EquipmentRegistrationFields>`（仕様 2.6）に一本化した。
 * 技術に固有なのは「受領から登録」の発注元チップ・添付・貸出のための管理情報だけ。
 */
export const EngEquipmentFormDialog = ({ open, onClose, editing, onSaved, prefill = null, origin = null }) => {
  const [form, setForm] = useState(empty());
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [myDeptId, setMyDeptId] = useState('');
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (!open) return;
    engApi.projects().then(setProjects).catch(() => {});
    engApi.equipmentCategories().then(setCategories).catch(() => setCategories([]));
    engApi.employees()
      .then((list) => {
        const rows = Array.isArray(list) ? list : (list?.items || []);
        setEmployees(rows.filter((e) => (e.status || 'active') === 'active')
          .map((e) => ({ id: e.id, name: e.name || e.code || e.email || e.id })));
      })
      .catch(() => setEmployees([]));
    engApi.departments()
      .then((list) => {
        const rows = Array.isArray(list) ? list : (list?.items || []);
        setDepartments(rows);
        // 部門は「この画面の部門」を既定にする（技術のページ → 技術部）。
        const tech = rows.find((d) => String(d.name || '').includes('技術'));
        setMyDeptId(tech?.id || '');
      })
      .catch(() => setDepartments([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        ...empty(),
        ...Object.fromEntries(Object.entries(editing).filter(([, v]) => v !== null && v !== undefined)),
        acquisition_price: editing.acquisition_price != null ? String(editing.acquisition_price) : '',
        // 点検は月が正本。技術の旧「点検周期」は月へ読み替えて見せる。
        inspection_interval_months: editing.inspection_interval_months
          ?? ({ annual: 12, semiannual: 6, quarterly: 3, monthly: 1 }[editing.inspection_cycle] ?? ''),
        last_inspection_on: editing.last_inspection_on || editing.last_inspected_at || '',
        next_calibration_on: editing.next_calibration_on || editing.calibration_due || '',
        calibration_required: !!(editing.calibration_required || editing.calibration_due),
      });
      setFiles(editing.attachments || []);
    } else {
      setForm({ ...empty(), department_id: myDeptId, ...(prefill || {}) });
      setFiles([]);
    }
  }, [open, editing, prefill, myDeptId]);

  const save = async () => {
    // 必須は新規のときだけ **警告** する（保存は止めない — 仕様 4）。
    if (!String(form.name || '').trim()) { toast.error('「名称」を入力してください'); return; }
    setSaving(true);
    try {
      const num = (v) => (String(v ?? '').trim() === '' ? null : Number(v));
      const txt = (v) => (String(v ?? '').trim() || null);
      // 番号は保存の瞬間に確定する（ダイアログを閉じただけなら消費しない）。
      let numberFromRule = null;
      if (!editing) {
        try {
          numberFromRule = await finalizeNumber(form.number_value);
        } catch (e) {
          toast.error(e?.response?.data?.detail || '品番の発番に失敗しました');
          setSaving(false);
          return;
        }
      }
      const body = {
        asset_no: numberFromRule, sku_template_code: form.number_value?.template_code || null,
        sku_group_id: txt(form.sku_group_id) || txt(form.number_value?.sku_group_code),
        order_category_id: form.order_category_id || null,
        order_category_name: txt(form.order_category_name),
        uom: txt(form.uom),
        name: form.name.trim(), category: txt(form.category), kind: form.kind || 'machine',
        maker: txt(form.maker), maker_pn: txt(form.maker_pn), model_no: txt(form.model_no),
        serial_no: txt(form.serial_no), location: txt(form.location), location_id: form.location_id || null,
        department_id: form.department_id || null, status: form.status, owner: txt(form.owner),
        owner_employee_id: form.owner_employee_id || null,
        source_project_id: form.source_project_id || null,
        description: txt(form.description), reference_url: txt(form.reference_url),
        supplier_id: form.supplier_id || null, supplier_name: txt(form.supplier_name),
        acquisition_price: num(form.acquisition_price), acquired_on: form.acquired_on || null,
        inspection_interval_months: num(form.inspection_interval_months),
        last_inspected_at: form.last_inspection_on || null,
        next_inspection_on: form.next_inspection_on || null,
        inspection_owner: txt(form.inspection_owner),
        calibration_required: !!form.calibration_required,
        calibration_interval_months: num(form.calibration_interval_months),
        last_calibration_on: form.last_calibration_on || null,
        calibration_due: form.next_calibration_on || null,
        calibration_owner: txt(form.calibration_owner),
        attachments: files,
      };
      if (editing) {
        await engApi.updateEquipment(editing.id, body);
      } else {
        const created = await engApi.createEquipment(body);
        // 発注元は専用の origin エンドポイントで記録する（source_type/source_id は書かない）。
        if (origin?.source_type && origin?.source_id) {
          try {
            await engApi.setEquipmentOrigin(created.id, origin);
          } catch (e) {
            toast.warning(e?.response?.data?.detail || '設備は登録しましたが発注元の記録に失敗しました');
          }
        }
      }
      toast.success(editing ? '更新しました' : '登録しました');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="eng-equip-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? '設備を編集' : '設備を登録'}</DialogTitle>
        </DialogHeader>
        {/* 発注元は由来がある時だけ出す読み取り専用の参照。 */}
        {!editing && origin?.label && (
          <div className="rounded-lg border border-teal-200 bg-teal-50/50 px-3 py-2 text-[12px] text-zinc-700"
            data-testid="eng-equip-form-origin-chip">
            <span className="font-semibold text-teal-800">受領から登録</span>
            <span className="ml-2 text-zinc-600">発注元: {origin.label}</span>
          </div>
        )}

        <EquipmentRegistrationFields
          value={form} onChange={setForm} editing={!!editing}
          currentNo={editing?.equip_no || editing?.asset_no || '—'}
          askAcquisitionType
          register="equip" appCode="engineering"
          categories={categories} departments={departments} employees={employees}
          projects={projects} statusOptions={statusOptions(editing?.status)}
          locationDepartmentId={null}
          testPrefix="eng-equip-form"
          files={files} onFilesChange={setFiles} onUpload={engApi.uploadEquipmentFile}
        />


        <DialogFooter>
          <Button variant="outline" onClick={onClose}>キャンセル</Button>
          <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="eng-equip-form-save">
            {saving ? '保存中…' : '保存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
